import { IGif } from '@giphy/js-types'
import { Logger } from '@giphy/js-util'
import { map } from 'lodash'
import React, { FC, SyntheticEvent, useCallback } from 'react'
import { matchPath, useLocation } from 'react-router'
import routes, { getRouteFromPathname } from 'shared/routes'
import { googleAnalyticsEvent, trackPageView } from 'shared/util/analytics'
import { getGifId } from 'shared/util/url'
import GAEventContext from './ga-events'
import useSameObjectRef from 'shared/hooks/use-same-ref'

// creates map {routes.GIF_DETAIL: gif detail}
const categories = new Map(map(routes, (route, key) => [route, key.toLowerCase().replace(/_/g, ' ')]))

type Props = {}

const getCategoryFromPathname = (pathname: string) => {
    const route = getRouteFromPathname(pathname)
    switch (route) {
        case routes.GIF_DETAIL:
            return 'GIF Detail'
        case routes.SEARCH:
        case routes.EXPLORE:
        case routes.ERROR_404:
        case routes.CHANNEL:
        case routes.FAVORITES:
        case routes.DASHBOARD:
            return categories.get(route)
        default:
            console.error(`GA event manager, no pathname match for ${pathname}`)
            return route ? categories.get(route) : ''
    }
}

const GAEventContextManager: FC<Props> = ({ children }) => {
    const { pathname } = useLocation()
    const onGifClick = useCallback(
        (gif: IGif, _e: SyntheticEvent<HTMLElement, Event>) => {
            const category = getCategoryFromPathname(pathname)
            googleAnalyticsEvent({ category, label: gif.id, action: 'GIF Click' })
        },
        [pathname]
    )
    const onGifRightClick = useCallback(
        (gif: IGif, _e: SyntheticEvent<HTMLElement, Event>) => {
            const category = getCategoryFromPathname(pathname)
            googleAnalyticsEvent({ category, label: gif.id, action: 'GIF Right Click' })
        },
        [pathname]
    )
    const onGifFavorite = useCallback(
        (gif: IGif) => {
            const category = getCategoryFromPathname(pathname)
            googleAnalyticsEvent({ category, label: gif.id, action: 'Favorite' })
        },
        [pathname]
    )
    const onLinkCopy = useCallback(
        (gif: IGif) => {
            const category = getCategoryFromPathname(pathname)
            googleAnalyticsEvent({ category, label: gif.id, action: 'GIF Copy Link' })
        },
        [pathname]
    )
    const onPagination = useCallback(
        (page: number) => {
            if (page === 1) {
                // GTALogger reports this
                return
            }
            const route = getRouteFromPathname(pathname)
            let url = ''
            switch (route) {
                case routes.GIF_DETAIL: {
                    const id = getGifId(pathname)
                    if (!id) {
                        console.error(`Gif detail GA id not found in pathname: ${pathname}`)
                    } else {
                        url = `/gifs/${id}/related/page/${page}`
                    }
                    break
                }
                case routes.SEARCH: {
                    const match = matchPath<{ term: string }>(pathname, routes.SEARCH)
                    const term = match?.params?.term
                    if (!term) {
                        console.error(`Search GA term not found in pathname: ${pathname}`)
                    } else {
                        url = `/search/${term}/${page}`
                    }
                    break
                }
                case routes.EXPLORE: {
                    const match = matchPath<{ term: string }>(pathname, routes.EXPLORE)
                    const term = match?.params?.term
                    if (!term) {
                        console.error(`Explore GA term not found in pathname: ${pathname}`)
                    } else {
                        url = `/explore/${term}/${page}`
                    }
                    break
                }
                default:
                    break
            }
            if (url) {
                Logger.debug(`Pagination - track GA, url: ${url}`)
                trackPageView(url, false)
            } else {
                console.error(`No GA pagination for ${pathname}`)
            }
        },
        [pathname]
    )
    const value = useSameObjectRef({ onGifClick, onGifRightClick, onGifFavorite, onLinkCopy, onPagination })
    return <GAEventContext.Provider value={value}>{children}</GAEventContext.Provider>
}

export default GAEventContextManager
